/**
 * Floor-plan segmentation — trace everything a plan draws.
 *
 * The Digital Twin importer finds a printed unit CODE and drops a rectangle on
 * it. That works for a schedule of rectangular shops and fails completely on a
 * real mall plan, where units are angled, trapezoidal and radial. This module
 * reads the DRAWING instead: it treats ink as walls, floods the enclosed fills
 * to find rooms, traces each room's boundary, and simplifies the pixel
 * staircase back into wall lines.
 *
 * EVERY enclosed shape is kept, not only the ones that look leasable.
 *
 * An earlier version threw away anything that wasn't a colour-filled shop, on
 * the reasoning that a leasing tool only cares about leasable area. That is
 * true of the commercials and wrong about the model: the promenades, atria,
 * cores and back-of-house are most of a mall's floor, and a plate drawn without
 * them reads as a floor with holes punched through it. So the shape stays; what
 * changes is its KIND (see ./regionKinds), which decides whether it is leasable,
 * whether routes can cross it, and how it is drawn.
 *
 * Colour still carries real information — these plans wash shops by category and
 * leave circulation white — so it drives the PROPOSED kind. It no longer decides
 * what survives. Every guess is visible and overridable in the importer.
 *
 * Everything is deliberately plain typed-array work so it can run on a worker
 * or in a test harness without a DOM beyond ImageData.
 *
 * Pipeline:
 *   edges     -> find wall lines on the FULL-RESOLUTION sheet (see detectEdges)
 *   palette   -> the handful of fill colours the sheet actually uses
 *   classify  -> every pixel becomes WALL or a palette index
 *   erode     -> shave class boundaries so anti-aliasing cannot weld shapes
 *   delabel   -> printed unit numbers stop counting as walls
 *   despeck   -> pockets too small to be rooms are given to their host
 *   label     -> flood fill non-wall pixels, joining only matching colours
 *   regrow    -> hand the shaved pixels back to the nearest room
 *   trace     -> Moore-neighbour boundary walk per region
 *   simplify  -> Douglas-Peucker + short-edge merge back to wall lines
 *   classify  -> propose a kind from colour, enclosure and shape
 */

import {
    simplify, dropCollinear, mergeShortEdges, area, centroid, bounds,
    ensureWinding, orthogonalise, solidity,
} from './geometry';
import { packBits, isLeasable } from './regionKinds';

const WALL = -1;

const diagonalOf = (w, h) => Math.hypot(w, h);

export const DEFAULT_OPTIONS = {
    /** Pixels darker than this are ink: walls, hatching, text. 0..255. */
    wallLuminance: 105,
    /** How many distinct fill colours to look for. */
    paletteSize: 24,
    /** RGB distance within which a pixel counts as "that fill colour". */
    colorTolerance: 30,
    /**
     * Pixels on a class boundary are turned into wall before flooding.
     *
     * Every stroke on an anti-aliased plan carries a one-or-two pixel halo where
     * the ink fades into the fill. Those pixels are nobody's colour, and if they
     * are allowed to join the nearest fill they weld neighbouring shapes into a
     * single ribbon that snakes along every wall — which is how one arcade
     * trapezoid came back as a 207-corner polygon enclosing the whole ring.
     * Shaving the boundary costs a pixel of frontage and removes the weld.
     */
    edgeErosion: 1,
    /**
     * Regions smaller than this fraction of the sheet are not rooms.
     *
     * Below this size a region is the counter of a printed 0, the gap between
     * two digit strokes, a hatch speck or a dimension tick. They are not kept
     * as shapes — but they are not discarded either, because a discarded
     * pocket leaves a HOLE. That is what a unit number does to the unit it is
     * printed inside: the enclosed parts of the glyphs become pockets, the
     * pockets fall under this threshold, and the shop is left with its label
     * punched out of it and its floor area understated to match.
     *
     * So a speck is absorbed by whatever surrounds it instead. It stops being
     * a shape and becomes part of its host, which is what it always was.
     *
     * This was 0.0004, which is four times too big, and it was the single
     * largest source of missing units on a real sheet.
     *
     * The number has to sit below the SMALLEST room on the plan, and the arcade
     * of a real mall is full of radial wedges a fifth the size of an ordinary
     * shop. On a plan drawing about a hundred and seventy units, 0.0004 landed
     * above them: dozens of real tenancies were declared specks, unclaimed, and
     * then handed to whichever neighbour reached them first — which loses the
     * unit AND inflates the neighbour, so the floor still looks complete. On
     * that sheet the score went from 53% of units traced to their own shape to
     * 79% on this change alone.
     *
     * A fraction of the sheet is the right unit even though the value was
     * wrong: a glyph and the sheet both scale with resolution, so the ratio
     * between them does not move when the same drawing is exported larger.
     * 0.0001 says a room may be as small as a ten-thousandth of the plate,
     * which leaves better than an order of magnitude between the smallest
     * tenancy measured here and the largest glyph counter.
     */
    minAreaFraction: 0.0001,
    /**
     * Douglas-Peucker tolerance, as a fraction of the working raster's diagonal.
     *
     * This exists to take the STAIRCASE out of a raster trace -- the boundary
     * walk climbs one pixel at a time, so a straight wall at an angle comes back
     * as a flight of steps -- and for nothing else. The staircase is one pixel
     * high, so the tolerance wants to be a few pixels of the working raster,
     * which is what this now is: about 3px on the 3600-4200px raster the tracer
     * always works at.
     *
     * It was 0.004, which is six source pixels on a 1400px sheet. That is a
     * sixth of the width of an arcade unit, so the outlines did not merely lose
     * their staircase -- they lost their corners, and cut clean across the
     * curved wall of every radial shop. Measured against the ink the plan
     * actually draws, the traced boundary sat on the drawn line 90.6% of the
     * time; at this value, 97.6%, with each unit gaining about five corners.
     *
     * Tightening it further buys nothing: below about three pixels the
     * remaining error is the wall mask's own edge noise, and chasing that just
     * spends vertices tracking it. Measured flat from 2px to 6px.
     */
    simplifyFraction: 0.0007,
    /** Collapse wall segments shorter than this fraction of the diagonal. */
    minEdgeFraction: 0.001,
    /**
     * Ink islands smaller than this share of the diagonal are printed labels,
     * not walls, and are dissolved back into the fill they sit on. 0 disables.
     *
     * A plan prints each unit's number INSIDE the unit. Ink is wall, so a
     * number set across a narrow shop cuts it in half; both halves then fall
     * under the minimum shape size and BOTH are discarded, so a labelled unit
     * disappears while its unlabelled neighbour survives. Measured on an arcade
     * of thirty small wedges, that lost eighteen of them and shattered the rest
     * into fifty-one pieces.
     *
     * What separates a label from a wall is not size but CONNECTION. A demising
     * wall meets the unit outlines at both of its ends, so every wall on the
     * sheet belongs to one enormous connected network of ink. A printed number
     * floats in the middle of a fill, touching nothing. So the test is
     * isolation, and the threshold only has to be loose enough to cover a glyph
     * and tight enough not to swallow some genuinely isolated structure — it is
     * nowhere near any real wall, which is part of a network thousands of
     * pixels across.
     *
     * A glyph that happens to touch a wall joins the network and is kept. That
     * costs a notch out of the unit's outline and nothing else, which is the
     * right way round to be wrong.
     *
     * Generous, because it is only a backstop. The decisive test is
     * labelPurity below: sizing this to a glyph does not work, since the edge
     * pass thickens each stroke until a three-digit number welds into one blob
     * several times the height of its own type.
     */
    labelSpanFraction: 0.08,
    /**
     * Share of an ink island's neighbours that must be the SAME fill for the
     * island to count as something printed on that fill.
     *
     * This is what separates a unit number from a kiosk standing in the
     * promenade. Both are ink islands of roughly the same size. The number is
     * printed ON one fill and has that fill on every side of it; the kiosk is
     * an outline with its own interior, so it has the promenade on the outside
     * and something else within, and dissolving it would erase a tenancy.
     *
     * Not 1.0, because anti-aliasing leaves the odd stray pixel of another
     * class against any stroke on the sheet.
     */
    labelPurity: 0.95,
    /**
     * Join two ink islands into one printed label when they are within this
     * many of their own heights of each other. See groupLabels.
     *
     * Sized to type rather than to the sheet: the space between characters of a
     * word is a fraction of their height, and the space between two units' own
     * numbers is a whole unit.
     */
    labelGroupGap: 2.5,
    /**
     * Two labels in one shape only mean two units if they are this far apart,
     * as a share of the shape's diagonal.
     *
     * A shop with its number printed above its trading name carries two labels
     * and is one unit; they sit together in the middle of it. Two units traced
     * as one carry their labels at opposite ends. Distance is the whole of the
     * difference.
     */
    labelSplitSpread: 0.35,
    /** Snap near-axis walls square. Off: mall plans are full of real angles. */
    squareUp: false,
    /**
     * Longest edge of the working raster.
     *
     * Sized by the tightest thing on a mall plan: a small unit with its number
     * printed inside it. Those two marks have to stay separable, and at 1600 on
     * a dense arcade they are not — a wedge is thirty-odd pixels across, the
     * label welds to the walls either side, and the unit is traced as two
     * halves. Raising this until the label comes free took an arcade of thirty
     * small wedges from twenty-eight traced whole to all thirty.
     *
     * Past that it stops paying: the label is already an island and more pixels
     * only cost time.
     */
    maxWorkingEdge: 4200,
    /**
     * Shortest the working raster is allowed to be. Small sheets are drawn UP
     * to this before anything is traced.
     *
     * Resampling adds no detail, and this is not pretending it does. What it
     * adds is room for the pipeline's own machinery to work in, because almost
     * every step of it is quantised to whole pixels: the erosion that keeps two
     * shops apart shaves one pixel, the wall mask claims another, the boundary
     * walk needs eight pixels of contour before it will trace anything, and the
     * shortest edge worth keeping is measured in pixels too. On a mall plan
     * exported at 1408px an arcade wedge is fourteen pixels across, so those
     * fixed costs are most of it, and the unit does not survive them.
     *
     * The user's own sheet is 1408 wide. Traced at that size, 63% of its units
     * came back as their own shape; drawn up threefold first, 79% — with
     * nothing else changed and no new information anywhere in the file.
     */
    minWorkingEdge: 3600,
    /**
     * ... but never enlarge a sheet by more than this.
     *
     * Past about three times, interpolation is inventing pixels between pixels
     * and every pass downstream simply costs more to reach the same answer.
     */
    maxUpscale: 3,
    /**
     * Longest edge of the raster the WALL LINES are found on.
     *
     * Deliberately much larger than the working raster, because these are two
     * different jobs. Reading fill colour wants a small raster: a wash is
     * hundreds of pixels across and averaging only makes it cleaner. Finding a
     * demising line wants a large one, because that line is ONE pixel wide and
     * averaging destroys it — see detectEdges.
     */
    detailEdge: 4800,
    /**
     * How big a step in luminance counts as a wall line, 0..255. 0 disables the
     * full-resolution pass entirely and falls back to the ink threshold alone.
     *
     * Reads in luminance units directly: 14 means "a line at least 14 of 255
     * darker or lighter than the fill either side of it".
     *
     * This is a FLOOR, not the value used: a noisy sheet raises it (see
     * noiseMultiple), because on such a sheet a step this small is not a line.
     */
    edgeSensitivity: 14,
    /**
     * Raise the wall threshold to this multiple of the sheet's own noise floor.
     *
     * A scanned or JPEG-exported plan carries speckle in every flat wash, and
     * speckle has gradients. Held at a fixed threshold, a noisy sheet reads
     * those gradients as walls and shatters one shop into a dozen slivers —
     * measured at +-12 of JPEG noise, a 270-shop plan came back with 562
     * tenancies. Raising the threshold by hand is not a reasonable thing to ask
     * of the operator, because the noise is invisible at the zoom they are
     * looking at and the symptom appears three steps later.
     *
     * So the sheet is asked instead. The MEDIAN gradient across a floor plan is
     * a direct measurement of its noise floor, since well over half of any plan
     * is flat fill; anything that stands well clear of that median is a line.
     * Chosen against measured noise: this tracks the threshold that was needed
     * at +-6, +-12 and +-20 without touching a clean sheet, where the median
     * gradient is zero and the floor above applies unchanged.
     */
    noiseMultiple: 4.5,
    /**
     * Keep a gradient this share of the threshold if it JOINS one that cleared it.
     *
     * A demising line is not equally distinct along its whole length. It passes
     * behind a printed number, it fades where the wash either side of it changes,
     * and a resampled sheet thins it wherever the line fell between two pixels.
     * Judged pixel by pixel it is a dashed line, and a dashed line does not
     * separate anything: the two shops flood into one another through the gaps
     * and are traced as a single shape.
     *
     * So the threshold above decides where a line certainly IS, and this decides
     * how far that certainty carries along it. A weaker gradient touching a
     * confirmed one is the same line continuing; a weaker gradient touching
     * nothing is the sheet's own speckle and is dropped, which is what keeps this
     * from re-admitting the noise the threshold exists to reject.
     *
     * 0 disables the linking and judges every pixel on its own.
     */
    edgeLinkRatio: 0.45,
    /**
     * Give each room back this many pixels of the wall it was shaved out of.
     *
     * Erosion and the edge mask both cut a room back from the line that bounds
     * it, and both are needed to keep neighbours apart. Left there, every
     * tenancy reads two or three pixels short on all four sides — which on a
     * plan where a shop is thirty pixels wide is a fifth of its area, silently
     * missing from the rent roll. Regrowing to the wall CENTRELINE is also how
     * GLA is actually measured, so this is the correct answer and not merely a
     * correction.
     *
     * Growth is self-limiting — a room can only claim wall pixels no other room
     * has claimed — so this saturates rather than running away, and passes
     * beyond the wall's own thickness measure identically. Against a fixture of
     * shops of known size it moved the traced area from 11% short to under 3%
     * short, the remainder being outline smoothing rather than anything
     * recoverable here.
     */
    regrowPasses: 3,

    /** Use fill colour to propose a kind. Off: everything is proposed a unit. */
    colouredOnly: true,
    /** Saturation floor for "this fill is a colour, not paper". 0..1. */
    minChroma: 0.09,
    /** Anything brighter than this is paper even if it has a colour cast. */
    paperLuminance: 238,
    /** A coloured shape below this solidity is proposed as back-of-house. */
    minSolidity: 0.45,
    /** A coloured shape longer than this many times its width, likewise. */
    maxAspect: 12,
    /** A neutral shape this much darker than the brightest paper reads as void. */
    voidLuminanceDrop: 16,
    /**
     * A shape bigger than this share of the sheet is too big to be one shop.
     *
     * Colour alone cannot tell a mall floor from a tenancy, because not every
     * plan follows the convention the colour rule assumes. The Yas sheet floors
     * its promenade in PALE BLUE, which carries enough chroma to read as a
     * category wash, so the mall itself was being proposed as a retail unit —
     * one shape covering a fifth of the floor. Size and shape settle that
     * without reference to the palette: a shop is a small compact cell, and a
     * promenade is a large one that snakes.
     */
    maxTenancyArea: 0.04,
    /**
     * Nothing covering more than this share of the sheet is a tenancy, whatever
     * else it looks like.
     *
     * The rule above wants a shape to be sprawling or hollow before it stops
     * being a shop, which a plain sheet of paper is neither: a plan whose floor
     * runs to the edge of the image comes back as one tidy rectangle covering
     * everything, and was being proposed as a retail unit the size of the whole
     * drawing. No floor plate is one shop.
     */
    maxAnyTenancyArea: 0.35,
    /**
     * How much of its own outline a shape must actually fill to be one room.
     *
     * A traced outline is an OUTER contour, so a promenade with shops punched
     * out of it comes back as a near-rectangle wrapping the whole floor — hull
     * solidity sees a tidy quadrilateral and reports nothing wrong. Comparing
     * the region's pixel count against the area its outline encloses is what
     * exposes the difference: a shop fills essentially all of its outline, a
     * mall floor only a fraction of one.
     */
    minFillRatio: 0.7,
    /**
     * A neutral shape smaller than this share of the sheet may still be a shop.
     *
     * The mirror of the problem above. The same sheet draws unit 117 in the
     * grey it uses for the ground outside, and unit 164 in a cream brighter
     * than the paper threshold, so both were classed as floor and neither
     * appeared in the rent roll — rent-bearing area silently missing, which is
     * the worst way for this to be wrong. A small compact enclosed cell is
     * proposed as a shop whatever colour it is drawn in.
     */
    maxNeutralTenancyArea: 0.012,

    /** Resolution of the grid used for the enclosure test and for routing. */
    routeGrid: 168,
    /**
     * How many of eight rays cast from a neutral cell must strike a coloured
     * shape for that cell to count as being inside the building. 0..8.
     *
     * Two simpler rules were tried and are both wrong. "Neutral space that runs
     * off the sheet edge is outside the building" fails because a plan is
     * usually a CROP — its promenades reach the edge of the image and would all
     * be classed as car park. "Neutral space within a corridor's reach of a
     * shopfront" fails in the other direction: the middle of a wide promenade is
     * further than that from any shop.
     *
     * What is actually true of a mall is that it is ENCLOSED BY SHOPFRONTS. Look
     * outward from a point in the promenade and you see shops in most
     * directions; do the same from the car park and you mostly see the edge of
     * the sheet. That handles wide atria, concave wings and L-shaped buildings
     * without a size threshold anywhere.
     */
    minEnclosureRays: 5,
    /**
     * Share of a neutral region's cells that must read as enclosed before the
     * region is PROPOSED as mall floor.
     *
     * Deliberately low, because a plan usually floods the promenade and the
     * apron outside the building into one region — they meet around the ends of
     * the parades. Judged as a whole that region is mostly outdoors and scores
     * badly, and demanding a majority classes the entire mall as car park.
     *
     * So the region-level score only decides what the shape is CALLED. Which
     * part of it can actually be walked is decided cell by cell, by the inside
     * mask below, and routes never leave the building even when the shape that
     * carries them extends past it.
     */
    minEnclosedShare: 0.12,
};

/**
 * Find wall lines on the full-resolution sheet.
 *
 * This exists because of how a mall plan draws a demising wall: as a HAIRLINE,
 * one pixel wide on a sheet three or four thousand pixels across. Downscale
 * that sheet to the working raster and the line is averaged into the fill
 * either side of it — a 1px line inside a 6px box comes back at a sixth of its
 * contrast — and an absolute ink threshold can no longer see it. The shops it
 * divided then flood into one another, and a parade of thirteen tenancies is
 * traced as a single shape. That is exactly how a floor came back carrying a
 * 19,298 m2 "unit".
 *
 * Measured on a synthetic plan at realistic density, a soft grey hairline
 * survived the downscale on 2% of shops. The line has to be found BEFORE the
 * sheet is resampled, which is what this does.
 *
 * It is a Sobel gradient rather than a second ink threshold because contrast,
 * not darkness, is what makes a line a line. A plan may draw its demising walls
 * in grey on a purple wash, or in white on a dark one, and neither is ink by
 * any absolute measure — but both are a step. Dividing the magnitude by the
 * kernel weight makes the threshold read in luminance units, so the control is
 * "how big a step counts", which is a question about the drawing rather than
 * about the filter.
 *
 * The mask is folded down to the working raster with OR, not an average: a wall
 * anywhere in a cell makes the whole cell wall. Erring towards wall splits a
 * room that should not have been split, which the operator can see and merge;
 * erring the other way welds two rooms into one, which looks plausible and is
 * silently wrong.
 *
 * @param {{data:Uint8ClampedArray,width:number,height:number}} detail full-res sheet
 * @param {object} [report] filled in with the threshold and noise floor used
 * @returns {Uint8Array|null} 1 = wall, at targetW x targetH
 */
export function detectEdges(detail, targetW, targetH, options = {}, report = null) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    if (!(opts.edgeSensitivity > 0)) return null;

    const { data, width, height } = detail;
    if (width < 3 || height < 3) return null;

    // Luminance plane first: the gradient is taken on brightness, so a coloured
    // line on a coloured fill registers as long as the two differ in value.
    const lum = new Float32Array(width * height);
    for (let i = 0, p = 0; i < lum.length; i++, p += 4) {
        lum[i] = (0.2126 * data[p]) + (0.7152 * data[p + 1]) + (0.0722 * data[p + 2]);
    }

    // Gradient magnitude in luminance units (/4 is the Sobel kernel weight), so
    // both the histogram below and the threshold read on the same scale as the
    // pixel values themselves.
    const mag = new Float32Array(width * height);
    const hist = new Uint32Array(256);
    for (let y = 1; y < height - 1; y++) {
        const row = y * width;
        const up = row - width;
        const dn = row + width;
        for (let x = 1; x < width - 1; x++) {
            const gx = (lum[up + x + 1] + (2 * lum[row + x + 1]) + lum[dn + x + 1])
                - (lum[up + x - 1] + (2 * lum[row + x - 1]) + lum[dn + x - 1]);
            const gy = (lum[dn + x - 1] + (2 * lum[dn + x]) + lum[dn + x + 1])
                - (lum[up + x - 1] + (2 * lum[up + x]) + lum[up + x + 1]);
            const m = Math.max(Math.abs(gx), Math.abs(gy)) / 4;
            mag[row + x] = m;
            hist[Math.min(255, m | 0)] += 1;
        }
    }

    // Median gradient = the sheet's noise floor, since most of a plan is flat
    // fill. Zero on clean line art; several units on a JPEG export.
    let seen = 0;
    let noise = 0;
    const half = ((width - 2) * (height - 2)) / 2;
    for (let i = 0; i < 256; i++) {
        seen += hist[i];
        if (seen >= half) { noise = i; break; }
    }

    const threshold = Math.max(opts.edgeSensitivity, noise * opts.noiseMultiple);
    const weak = threshold * Math.max(0, Math.min(1, opts.edgeLinkRatio));

    /*
     * Two thresholds, then follow the line.
     *
     * Anything over the threshold is a line. Anything over the weaker one is a
     * line ONLY if it can be reached from a pixel that was, walking along the
     * gradient — so a demising line that fades behind a printed number carries
     * its certainty across the gap, while an isolated flicker of speckle, which
     * touches nothing, is dropped. See edgeLinkRatio.
     */
    const STRONG = 2;
    const mark = new Uint8Array(width * height);
    let seeds = 0;
    let candidates = 0;
    for (let y = 1; y < height - 1; y++) {
        const row = y * width;
        for (let x = 1; x < width - 1; x++) {
            const m = mag[row + x];
            if (m >= threshold) { mark[row + x] = STRONG; seeds += 1; candidates += 1; }
            else if (weak > 0 && m >= weak) { mark[row + x] = 1; candidates += 1; }
        }
    }

    if (seeds && candidates > seeds) {
        const queue = new Int32Array(candidates);
        let head = 0;
        let tail = 0;
        for (let i = 0; i < mark.length; i++) if (mark[i] === STRONG) queue[tail++] = i;
        while (head < tail) {
            const idx = queue[head++];
            const x = idx % width;
            const y = (idx - x) / width;
            for (let dy = -1; dy <= 1; dy++) {
                const ny = y + dy;
                if (ny < 1 || ny >= height - 1) continue;
                for (let dx = -1; dx <= 1; dx++) {
                    const nx = x + dx;
                    if ((dx === 0 && dy === 0) || nx < 1 || nx >= width - 1) continue;
                    const n = (ny * width) + nx;
                    if (mark[n] !== 1) continue;
                    mark[n] = STRONG;
                    queue[tail++] = n;
                }
            }
        }
    }

    if (report) {
        report.threshold = threshold;
        report.noise = noise;
        report.seeded = seeds;
    }

    const out = new Uint8Array(targetW * targetH);
    const sx = targetW / width;
    const sy = targetH / height;
    let kept = 0;
    for (let y = 1; y < height - 1; y++) {
        const row = y * width;
        for (let x = 1; x < width - 1; x++) {
            if (mark[row + x] !== STRONG) continue;
            kept += 1;
            const tx = (x * sx) | 0;
            const ty = (y * sy) | 0;
            if (tx < targetW && ty < targetH) out[(ty * targetW) + tx] = 1;
        }
    }
    if (report) report.linked = kept - seeds;

    return out;
}


/**
 * Downscale into a working raster so segmentation cost is bounded, and carry
 * down a wall mask found at full resolution alongside it.
 *
 * The returned object is ImageData-shaped plus an `edges` field, so every
 * existing consumer keeps working and only segmentPlan looks at the extra.
 */
export function toWorkingImageData(source, maxEdge = DEFAULT_OPTIONS.maxWorkingEdge, options = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options, maxWorkingEdge: maxEdge };
    const sw = source.width;
    const sh = source.height;

    const drawAt = (w, h) => {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(source, 0, 0, w, h);
        return ctx.getImageData(0, 0, w, h);
    };

    /*
     * Down to the cap if the sheet is bigger than it, UP to the floor if it is
     * smaller -- but never enlarged more than maxUpscale, which is the point
     * past which resampling stops buying anything. See minWorkingEdge.
     */
    const longest = Math.max(sw, sh);
    const target = Math.min(
        maxEdge,
        Math.max(longest, Math.min(opts.minWorkingEdge || 0, longest * (opts.maxUpscale || 1))),
    );
    const scale = target / longest;
    const w = Math.max(1, Math.round(sw * scale));
    const h = Math.max(1, Math.round(sh * scale));
    const working = drawAt(w, h);

    /*
     * A second, finer raster is only worth drawing when the sheet actually has
     * more detail to give. When it does not, the gradient pass still runs -- on
     * the working raster itself.
     *
     * It used to be skipped entirely in that case, which quietly turned the
     * hairline detector off for every plan exported at 2000-odd pixels: the
     * working raster is capped at 2400, so such a sheet is its own detail
     * raster and failed the "is there more to be had" test. Those plans fell
     * back to the ink threshold alone, and a demising line drawn as a soft
     * tint -- too pale to be ink, too close to the wash to be a different
     * colour -- was invisible. Measured on a parade of nine shops divided by
     * such lines, all nine came back as ONE shape.
     *
     * The finer raster is better when it exists, and the working raster is
     * better than nothing, which is what this was doing.
     */
    const dScale = Math.min(1, opts.detailEdge / Math.max(sw, sh));
    const dw = Math.max(1, Math.round(sw * dScale));
    const dh = Math.max(1, Math.round(sh * dScale));
    const report = {};
    const detail = (dw > w * 1.1) ? drawAt(dw, dh) : working;
    const edges = detectEdges(detail, w, h, opts, report);

    return {
        data: working.data,
        width: w,
        height: h,
        edges,
        edgeReport: edges
            ? { ...report, detailWidth: detail.width, detailHeight: detail.height }
            : null,
    };
}

/**
 * Find the fill colours the sheet actually uses.
 *
 * Bit-shift bucketing (the previous approach) splits one flat wash across two
 * buckets the moment JPEG noise or a gradient nudges a channel over a bucket
 * boundary, which shatters a single shop into slivers — the "weird shapes".
 * Building a palette from the histogram and snapping to it instead means every
 * pixel of a wash lands on the same index no matter how noisy it is.
 */
function buildPalette(data, opts) {
    // 5 bits per channel: coarse enough to survive noise, fine enough to keep
    // two similar category washes apart.
    const hist = new Uint32Array(32768);
    const sumR = new Float64Array(32768);
    const sumG = new Float64Array(32768);
    const sumB = new Float64Array(32768);

    for (let p = 0; p < data.length; p += 4) {
        if (data[p + 3] < 32) continue;
        const r = data[p];
        const g = data[p + 1];
        const b = data[p + 2];
        if ((0.2126 * r) + (0.7152 * g) + (0.0722 * b) < opts.wallLuminance) continue;
        const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
        hist[key] += 1;
        sumR[key] += r;
        sumG[key] += g;
        sumB[key] += b;
    }

    const order = [];
    for (let i = 0; i < hist.length; i++) if (hist[i]) order.push(i);
    order.sort((a, b) => hist[b] - hist[a]);

    const palette = [];
    const minSep = opts.colorTolerance * 1.25;
    for (let i = 0; i < order.length && palette.length < opts.paletteSize; i++) {
        const k = order[i];
        const n = hist[k];
        const c = [sumR[k] / n, sumG[k] / n, sumB[k] / n];
        // Greedy: only admit a colour that is visibly different from every
        // colour already admitted, so the palette spans the sheet's washes
        // rather than 24 shades of the same white.
        let tooClose = false;
        for (let j = 0; j < palette.length; j++) {
            const q = palette[j].rgb;
            if (Math.hypot(c[0] - q[0], c[1] - q[1], c[2] - q[2]) < minSep) { tooClose = true; break; }
        }
        if (tooClose) continue;
        palette.push({ rgb: c, count: n });
    }

    if (!palette.length) palette.push({ rgb: [255, 255, 255], count: 1 });
    return palette;
}

const chromaOf = ([r, g, b]) => (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
const lumOf = ([r, g, b]) => (0.2126 * r) + (0.7152 * g) + (0.0722 * b);

/**
 * Classify every pixel as WALL or a palette index.
 * Alpha-zero pixels count as wall so transparent PNG margins don't flood.
 */
function classify(imageData, palette, opts) {
    const { data, width, height } = imageData;
    const edges = imageData.edges || null;
    const cls = new Int32Array(width * height);
    // Tight on assignment. A pixel has to actually BE one of the sheet's fill
    // colours; a half-blended edge pixel is wall, because that is what it is.
    const maxDist = opts.colorTolerance * 1.15;

    for (let i = 0, p = 0; i < cls.length; i++, p += 4) {
        if (data[p + 3] < 32) { cls[i] = WALL; continue; }
        // A line found at full resolution is a wall whatever the averaged
        // pixel now looks like.
        if (edges && edges[i]) { cls[i] = WALL; continue; }
        const r = data[p];
        const g = data[p + 1];
        const b = data[p + 2];
        if ((0.2126 * r) + (0.7152 * g) + (0.0722 * b) < opts.wallLuminance) { cls[i] = WALL; continue; }

        let best = -1;
        let bestD = Infinity;
        for (let j = 0; j < palette.length; j++) {
            const q = palette[j].rgb;
            const dr = r - q[0];
            const dg = g - q[1];
            const db = b - q[2];
            const d = (dr * dr) + (dg * dg) + (db * db);
            if (d < bestD) { bestD = d; best = j; }
        }
        cls[i] = (bestD <= maxDist * maxDist) ? best : WALL;
    }
    return cls;
}

/**
 * Turn any pixel that touches a different class into WALL,  times.
 *
 * This is a morphological erosion of every colour class at once. Besides
 * clearing anti-aliasing halos it separates two same-coloured shops that touch
 * through a one-pixel break in the wall line, which would otherwise flood into
 * a single tenancy.
 */
function erodeClassBoundaries(cls, width, height, passes) {
    for (let p = 0; p < passes; p++) {
        const src = cls.slice();
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width) + x;
                const v = src[i];
                if (v === WALL) continue;
                if ((x > 0 && src[i - 1] !== v)
                    || (x < width - 1 && src[i + 1] !== v)
                    || (y > 0 && src[i - width] !== v)
                    || (y < height - 1 && src[i + width] !== v)) cls[i] = WALL;
            }
        }
    }
}

/**
 * Dissolve printed labels back into the fill they are printed on.
 *
 * Walks the ink as 8-connected components and dissolves the ones that are
 * ISLANDS: connected to no wall network, and sitting on a single fill. Each is
 * handed the fill class its neighbours have, which for a number printed inside
 * a shop is that shop.
 *
 * Eight-connected on purpose: a diagonal stroke is one glyph, and splitting it
 * into a staircase of separate components would be counting the same mark
 * several times.
 *
 * Returns one pixel of each island as well as the count. Once the island has
 * been dissolved that pixel belongs to whichever shape the number was printed
 * inside, so it is how the trace can afterwards say how many unit numbers ended
 * up in one shape -- which is the strongest evidence there is that two units
 * were traced as one. See flagRegions.
 */
function dissolveLabels(cls, width, height, maxSpan, purity) {
    const seeds = [];
    if (!(maxSpan > 0)) return { count: 0, seeds };

    const seen = new Uint8Array(width * height);
    const stack = new Int32Array(width * height);
    const pixels = [];
    let dissolved = 0;

    for (let seed = 0; seed < cls.length; seed++) {
        if (cls[seed] !== WALL || seen[seed]) continue;

        let sp = 0;
        stack[sp++] = seed;
        seen[seed] = 1;
        pixels.length = 0;
        let minX = width; let minY = height; let maxX = -1; let maxY = -1;
        let tooBig = false;

        while (sp > 0) {
            const idx = stack[--sp];
            const x = idx % width;
            const y = (idx - x) / width;
            pixels.push(idx);
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
            // Once the component is provably a wall network there is nothing to
            // decide, but the walk still has to finish so its pixels are marked
            // seen and not re-walked from another seed.
            if (!tooBig && Math.max(maxX - minX, maxY - minY) > maxSpan) tooBig = true;

            for (let dy = -1; dy <= 1; dy++) {
                const ny = y + dy;
                if (ny < 0 || ny >= height) continue;
                for (let dx = -1; dx <= 1; dx++) {
                    const nx = x + dx;
                    if ((dx === 0 && dy === 0) || nx < 0 || nx >= width) continue;
                    const n = (ny * width) + nx;
                    if (seen[n] || cls[n] !== WALL) continue;
                    seen[n] = 1;
                    stack[sp++] = n;
                }
            }
        }

        if (tooBig) continue;

        // Whatever this island is printed on, by majority of its neighbours.
        const votes = new Map();
        for (let i = 0; i < pixels.length; i++) {
            const idx = pixels[i];
            const x = idx % width;
            const y = (idx - x) / width;
            for (let dy = -1; dy <= 1; dy++) {
                const ny = y + dy;
                if (ny < 0 || ny >= height) continue;
                for (let dx = -1; dx <= 1; dx++) {
                    const nx = x + dx;
                    if ((dx === 0 && dy === 0) || nx < 0 || nx >= width) continue;
                    const v = cls[(ny * width) + nx];
                    if (v === WALL) continue;
                    votes.set(v, (votes.get(v) || 0) + 1);
                }
            }
        }
        if (!votes.size) continue;

        let best = WALL;
        let bestN = -1;
        let total = 0;
        votes.forEach((n, v) => { total += n; if (n > bestN) { bestN = n; best = v; } });

        // Printed ON one fill, or an outline with something different inside it?
        // Only the first is a label.
        if (bestN / total < purity) continue;

        for (let i = 0; i < pixels.length; i++) cls[pixels[i]] = best;
        // The bounds come along because a single printed number is several of
        // these -- one per digit, unless the strokes happen to touch -- and
        // they have to be gathered back into one label before they can be
        // counted. See groupLabels.
        seeds.push({ idx: pixels[0], minX, minY, maxX, maxY });
        dissolved += 1;
    }

    return { count: dissolved, seeds };
}

/**
 * Gather scattered ink islands back into the labels they were printed as.
 *
 * One printed number is not one island. "164" is three, unless the edge pass
 * happens to have thickened the strokes until they touch, and whether it does
 * depends on the resolution of the sheet -- so counting islands directly would
 * report a three-digit unit as three labels and flag every one of them.
 *
 * What separates two digits of one number from two numbers in two different
 * units is DISTANCE, measured against the type rather than against the sheet:
 * characters of a word sit within a couple of glyph-heights of each other, and
 * the labels of two units sit a unit apart. So islands are joined by
 * single-linkage at a multiple of their own height, which is scale-free and
 * needs no knowledge of the font.
 *
 * Also returns how far apart the resulting labels are as a share of the shape's
 * own diagonal. Two labels close together in a big shape are a unit number with
 * its tenant name printed under it; two labels at opposite ends of it are two
 * units. Without that distinction every shop whose plan prints a name as well
 * as a number would be reported as two units joined.
 */
function groupLabels(islands, gapFactor, shapeDiagonal) {
    const n = islands.length;
    if (!n) return { count: 0, spread: 0 };

    const parent = new Array(n);
    for (let i = 0; i < n; i++) parent[i] = i;
    const find = (i) => { let r = i; while (parent[r] !== r) r = parent[r]; parent[i] = r; return r; };

    for (let i = 0; i < n; i++) {
        const a = islands[i];
        for (let j = i + 1; j < n; j++) {
            const b = islands[j];
            // Gap between the boxes, zero where they overlap on that axis.
            const dx = Math.max(0, Math.max(a.minX - b.maxX, b.minX - a.maxX));
            const dy = Math.max(0, Math.max(a.minY - b.maxY, b.minY - a.maxY));
            const h = Math.max(a.maxY - a.minY, b.maxY - b.minY) + 1;
            if (Math.hypot(dx, dy) > gapFactor * h) continue;
            const ra = find(i);
            const rb = find(j);
            if (ra !== rb) parent[ra] = rb;
        }
    }

    const centres = new Map();
    for (let i = 0; i < n; i++) {
        const r = find(i);
        const a = islands[i];
        const c = centres.get(r) || { x: 0, y: 0, n: 0 };
        c.x += (a.minX + a.maxX) / 2;
        c.y += (a.minY + a.maxY) / 2;
        c.n += 1;
        centres.set(r, c);
    }
    const pts = [...centres.values()].map((c) => [c.x / c.n, c.y / c.n]);

    let spread = 0;
    for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
            spread = Math.max(spread, Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1]));
        }
    }
    return {
        count: pts.length,
        spread: shapeDiagonal > 0 ? +(spread / shapeDiagonal).toFixed(3) : 0,
    };
}

/** Iterative 4-connected flood fill. Returns labels + per-label stats. */
function labelRegions(cls, width, height) {
    const labels = new Int32Array(width * height).fill(-1);
    const regions = [];
    const stack = new Int32Array(width * height);

    for (let seed = 0; seed < cls.length; seed++) {
        if (cls[seed] === WALL || labels[seed] !== -1) continue;

        const id = regions.length;
        const bucket = cls[seed];
        let sp = 0;
        stack[sp++] = seed;
        labels[seed] = id;

        let count = 0;
        let minX = width; let minY = height; let maxX = -1; let maxY = -1;
        let touchesBorder = false;

        while (sp > 0) {
            const idx = stack[--sp];
            const x = idx % width;
            const y = (idx - x) / width;
            count += 1;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
            if (x === 0 || y === 0 || x === width - 1 || y === height - 1) touchesBorder = true;

            if (x > 0) { const n = idx - 1; if (labels[n] === -1 && cls[n] === bucket) { labels[n] = id; stack[sp++] = n; } }
            if (x < width - 1) { const n = idx + 1; if (labels[n] === -1 && cls[n] === bucket) { labels[n] = id; stack[sp++] = n; } }
            if (y > 0) { const n = idx - width; if (labels[n] === -1 && cls[n] === bucket) { labels[n] = id; stack[sp++] = n; } }
            if (y < height - 1) { const n = idx + width; if (labels[n] === -1 && cls[n] === bucket) { labels[n] = id; stack[sp++] = n; } }
        }

        regions.push({ id, bucket, count, minX, minY, maxX, maxY, touchesBorder });
    }

    return { labels, regions };
}

/**
 * Give every pocket too small to be a room back to whatever encloses it.
 *
 * Unclaims their pixels rather than deleting them, so the regrow below hands
 * them to the surrounding region along with the wall. Returns how many were
 * absorbed, which is what the panel reports as specks.
 *
 * Handing each speck WHOLE to the room it shares the most edge with was tried
 * here, on the reasoning that unclaiming leaves a race the neighbouring shop
 * can win. It measured no better: fewer units lost, more of them merged into a
 * neighbour, no net gain. The reason is that when a printed label severs a
 * unit, EVERY piece of it is a speck, so there is no surviving parent left to
 * give them back to and the vote goes to whatever else is in reach. The fault
 * is upstream, in the label being kept as a wall at all.
 */
function dissolveSpecks(labels, regions, minPx) {
    let dead = 0;
    regions.forEach((r) => {
        if (r.count >= minPx) return;
        r.speck = true;
        dead += 1;
    });
    if (!dead) return 0;
    for (let i = 0; i < labels.length; i++) {
        const id = labels[i];
        if (id !== -1 && regions[id].speck) labels[i] = -1;
    }
    return dead;
}

/**
 * Hand shaved pixels back to the nearest room.
 *
 * Erosion and the edge mask both cut a room back from the line bounding it.
 * This walks the rooms outward into the unclaimed wall pixels, so two rooms
 * either side of a demising line meet at its centreline rather than each
 * stopping short of it. Rooms cannot merge here — labels are already fixed, so
 * this only restores area — and growth is bounded, so a room cannot escape
 * across a thick wall into the space beyond.
 *
 * Region counts and bounds are recomputed afterwards, since the size filter and
 * the boundary trace both read them.
 */
function growRegions(labels, width, height, regions, passes) {
    if (!(passes > 0) || !regions.length) return;

    for (let p = 0; p < passes; p++) {
        const src = labels.slice();
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width) + x;
                if (src[i] !== -1) continue;
                /*
                 * Eight-connected, not four. Four-connected growth advances a
                 * corner at half the rate of a side, so a rectangular shop
                 * comes back with its corners chamfered off — still a quad,
                 * but no longer square, and measurably smaller. Growing on the
                 * Chebyshev neighbourhood keeps a rectangle rectangular.
                 *
                 * Nearest wins; ties go to whichever neighbour is scanned
                 * first, which across one pixel of wall is not a distinction
                 * that matters. Rooms cannot merge here whatever the tie —
                 * labels are already fixed.
                 */
                let pick = -1;
                for (let dy = -1; dy <= 1 && pick === -1; dy++) {
                    const ny = y + dy;
                    if (ny < 0 || ny >= height) continue;
                    for (let dx = -1; dx <= 1; dx++) {
                        const nx = x + dx;
                        if ((dx === 0 && dy === 0) || nx < 0 || nx >= width) continue;
                        const v = src[(ny * width) + nx];
                        if (v !== -1) { pick = v; break; }
                    }
                }
                if (pick !== -1) labels[i] = pick;
            }
        }
    }

    regions.forEach((r) => {
        r.count = 0;
        r.minX = width; r.minY = height; r.maxX = -1; r.maxY = -1;
    });
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const id = labels[(y * width) + x];
            if (id === -1) continue;
            const r = regions[id];
            r.count += 1;
            if (x < r.minX) r.minX = x;
            if (y < r.minY) r.minY = y;
            if (x > r.maxX) r.maxX = x;
            if (y > r.maxY) r.maxY = y;
        }
    }
}

// Moore neighbourhood, clockwise starting due west.
const MOORE = [
    [-1, 0], [-1, -1], [0, -1], [1, -1],
    [1, 0], [1, 1], [0, 1], [-1, 1],
];

/**
 * Moore-neighbour boundary trace.
 *
 * `b` is the current boundary pixel, `c` the backtrack (the OUTSIDE pixel we
 * arrived through). Checking the pixel alone is not enough to know we are home,
 * because a boundary that pinches to one pixel wide is legitimately visited
 * twice — so the walk has to recognise a repeated STEP, not a repeated pixel.
 *
 * The stop condition is that the first move repeats: the same departure pixel
 * followed by the same arrival pixel. Jacob's criterion (re-entering the start
 * from the same backtrack) looks equivalent and is not — on some contours the
 * start is re-entered through a different neighbour every lap, so the walk never
 * terminates and instead spirals until it exhausts its budget. Nothing crashes;
 * the contour just comes back with the same ring wound thirty times, which RDP
 * cannot simplify and whose shoelace area is thirty times too big. That is the
 * source of the 200-corner "weird shapes" with impossible floor areas.
 *
 * Returns pixel-space vertices, one per boundary pixel, in order.
 */
function traceBoundary(labels, width, height, id, startIdx, budget) {
    const inRegion = (x, y) => x >= 0 && y >= 0 && x < width && y < height && labels[(y * width) + x] === id;

    const sx = startIdx % width;
    const sy = (startIdx - sx) / width;

    let bx = sx;
    let by = sy;
    let cx = sx - 1;   // west of a top-left-most pixel is always outside
    let cy = sy;

    const startBx = bx;
    const startBy = by;

    // The first move, recorded once we have taken it. Repeating it means we
    // have come the whole way round.
    let firstToX = -1;
    let firstToY = -1;

    const contour = [[bx, by]];
    const maxSteps = Math.max(64, Math.min(budget, width * height));

    for (let step = 0; step < maxSteps; step++) {
        const fromX = bx;
        const fromY = by;
        let entry = -1;
        for (let i = 0; i < 8; i++) {
            if (bx + MOORE[i][0] === cx && by + MOORE[i][1] === cy) { entry = i; break; }
        }
        if (entry < 0) entry = 0;

        let found = false;
        for (let k = 1; k <= 8; k++) {
            const i = (entry + k) % 8;
            const nx = bx + MOORE[i][0];
            const ny = by + MOORE[i][1];
            if (inRegion(nx, ny)) {
                const prev = MOORE[(entry + k - 1) % 8];   // last outside pixel tested
                cx = bx + prev[0];
                cy = by + prev[1];
                bx = nx;
                by = ny;
                found = true;
                break;
            }
        }
        if (!found) break;   // isolated pixel

        if (firstToX < 0) { firstToX = bx; firstToY = by; }
        else if (fromX === startBx && fromY === startBy && bx === firstToX && by === firstToY) break;

        contour.push([bx, by]);
    }

    return contour;
}

/** Top-left-most pixel of a region — a guaranteed boundary start. */
function findStart(labels, width, region) {
    for (let y = region.minY; y <= region.maxY; y++) {
        for (let x = region.minX; x <= region.maxX; x++) {
            if (labels[(y * width) + x] === region.id) return (y * width) + x;
        }
    }
    return -1;
}

/** Trace + simplify one region into a normalised polygon, or null. */
function outlineOf(labels, width, height, region, epsilon, minEdge, squareUp) {
    const startIdx = findStart(labels, width, region);
    if (startIdx < 0) return null;

    const raw = traceBoundary(labels, width, height, region.id, startIdx, (region.count * 4) + 512);
    if (raw.length < 8) return null;

    let poly = simplify(raw, epsilon);

    // Close the ring BEFORE culling collinear vertices. A traced contour ends
    // one pixel from where it began, and RDP keeps both endpoints — so the ring
    // carries a near-duplicate vertex. Run dropCollinear first and it sees start
    // sitting on the line from that duplicate to the next corner, deletes it,
    // and a rectangle comes back as a TRIANGLE.
    if (poly.length > 3) {
        const first = poly[0];
        const last = poly[poly.length - 1];
        if (Math.hypot(first[0] - last[0], first[1] - last[1]) < Math.max(2, epsilon)) poly.pop();
    }
    if (poly.length < 3) return null;

    poly = dropCollinear(poly, epsilon * 0.35);
    poly = mergeShortEdges(poly, minEdge);
    poly = dropCollinear(poly, epsilon * 0.35);
    if (squareUp) poly = orthogonalise(poly, 8);
    if (poly.length < 3) return null;

    return ensureWinding(poly, true).map(([x, y]) => [x / width, y / height]);
}

const hexOf = ([r, g, b]) => {
    const h = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
    return `#${h(r)}${h(g)}${h(b)}`;
};

/**
 * Segment a plan image into classified regions.
 *
 * @param {ImageData} imageData working-resolution plan
 * @param {object} options see DEFAULT_OPTIONS
 * @returns {{ regions, palette, stats }} polygons NORMALISED to 0..1
 */
export function segmentPlan(imageData, options = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const { width, height, data } = imageData;
    const totalPx = width * height;

    const palette = buildPalette(data, opts);

    /*
     * Wall lines come from toWorkingImageData when it had a finer raster to
     * find them on. When it did not -- or when segmentPlan is called directly,
     * as the test harness does -- they are found here instead, on the raster in
     * hand. Finding them at all is what matters; finding them on a finer sheet
     * only makes them cleaner.
     */
    const edgeReport = {};
    const edges = imageData.edges || detectEdges(imageData, width, height, opts, edgeReport);
    const cls = classify(
        imageData.edges ? imageData : { data, width, height, edges },
        palette, opts,
    );
    erodeClassBoundaries(cls, width, height, opts.edgeErosion);
    // Before flooding, not after: once a printed number has cut a unit in two
    // there are two regions and no way left to tell that from a real wall.
    const printed = dissolveLabels(
        cls, width, height,
        opts.labelSpanFraction * Math.hypot(width, height),
        opts.labelPurity,
    );
    const labelsDissolved = printed.count;
    const { labels, regions } = labelRegions(cls, width, height);

    /*
     * How many printed numbers ended up inside each shape.
     *
     * Read here, straight after the flood and before anything is absorbed or
     * regrown, because this is the moment the assignment is exactly what the
     * flood decided. A shape holding two numbers is two units the trace joined;
     * a shape holding none, on a sheet that numbers its units, is a fragment of
     * one. Neither can be seen from the geometry alone -- both look like a
     * perfectly ordinary rectangle -- so this is the only place the evidence
     * exists.
     */
    const islandsByRegion = new Map();
    printed.seeds.forEach((island) => {
        const id = labels[island.idx];
        if (id < 0) return;
        if (!islandsByRegion.has(id)) islandsByRegion.set(id, []);
        islandsByRegion.get(id).push(island);
    });
    const labelling = new Map();
    islandsByRegion.forEach((islands, id) => {
        const r = regions[id];
        const diag = Math.hypot((r.maxX - r.minX) + 1, (r.maxY - r.minY) + 1);
        labelling.set(id, groupLabels(islands, opts.labelGroupGap, diag));
    });

    const minPx = opts.minAreaFraction * totalPx;
    const rejectedSmall = dissolveSpecks(labels, regions, minPx);
    /*
     * Enough passes to close over the largest thing that can be a speck, since
     * a pocket is absorbed from all sides at once. Growth is self-limiting and
     * two regions meeting simply stop, so overshooting the count costs nothing
     * but a little time.
     */
    const absorbPasses = rejectedSmall
        ? Math.ceil(Math.sqrt(minPx) / 2) + opts.regrowPasses
        : opts.regrowPasses;
    growRegions(labels, width, height, regions, absorbPasses);

    const diagonal = diagonalOf(width, height);
    const epsilon = opts.simplifyFraction * diagonal;
    const minEdge = opts.minEdgeFraction * diagonal;

    // Pass 1 — measure. Colour tells us whether a fill is a category wash or
    // paper; that is a proposal about kind, not a filter.
    const candidates = [];
    let brightestNeutral = 0;
    regions.forEach((region) => {
        if (region.speck) return;
        const rgb = palette[region.bucket] ? palette[region.bucket].rgb : [255, 255, 255];
        const chroma = chromaOf(rgb);
        const lum = lumOf(rgb);
        const coloured = opts.colouredOnly
            ? (chroma >= opts.minChroma && lum <= opts.paperLuminance)
            : true;
        if (!coloured && lum > brightestNeutral) brightestNeutral = lum;
        candidates.push({ region, rgb, chroma, lum, coloured });
    });

    // Pass 2 — enclosure, measured against the coloured shapes.
    const colouredIds = new Set(candidates.filter((c) => c.coloured).map((c) => c.region.id));

    /*
     * A sheet with no fills at all — a section cut from a 3D model, or an
     * architect's linework DXF — carries no colour information, so both the
     * chroma rule and the enclosure test that depends on it are meaningless:
     * every region scores zero and the whole floor is proposed as outdoors.
     *
     * On a line drawing the meaningful distinction is simpler. A room is an
     * enclosed cell that does not touch the edge of the sheet; the sheet edge is
     * where the outside is. Sectioned walls come back as very elongated cavities
     * and are caught by the aspect rule.
     */
    const lineworkOnly = colouredIds.size === 0;
    const { share: enclosed, inside } = enclosureByRegion(labels, width, height, colouredIds, opts);

    // Pass 3 — outline and classify.
    const out = [];
    candidates.forEach((c) => {
        const polygon = outlineOf(labels, width, height, c.region, epsilon, minEdge, opts.squareUp);
        if (!polygon) return;
        const polyArea = area(polygon);
        if (polyArea <= 0) return;

        const b = bounds(polygon);
        // Aspect and solidity in PIXELS — a normalised bbox on a non-square
        // sheet would distort both.
        const wPx = b.w * width;
        const hPx = b.h * height;
        const aspect = Math.max(wPx, hPx) / Math.max(1e-6, Math.min(wPx, hPx));
        const sol = solidity(polygon.map(([x, y]) => [x * width, y * height]));
        // How much of the outline the region genuinely occupies. Cheap, and the
        // only measure here that can see a shape with holes punched in it.
        const fillRatio = c.region.count / Math.max(1, polyArea * width * height);
        const share = enclosed.get(c.region.id) || 0;

        const kind = guessKind({
            lineworkOnly,
            coloured: c.coloured,
            lum: c.lum,
            brightestNeutral,
            enclosedShare: share,
            touchesBorder: c.region.touchesBorder,
            areaFraction: polyArea,
            aspect,
            solidity: sol,
            fillRatio,
        }, opts);

        out.push({
            id: `r${c.region.id}`,
            polygon,
            centroid: centroid(polygon),
            areaFraction: polyArea,
            pixelCount: c.region.count,
            color: hexOf(c.rgb),
            colorKey: hexOf(c.rgb),
            coloured: c.coloured,
            solidity: +sol.toFixed(3),
            fillRatio: +fillRatio.toFixed(3),
            aspect: +aspect.toFixed(2),
            enclosedShare: +share.toFixed(2),
            vertices: polygon.length,
            labelCount: (labelling.get(c.region.id) || { count: 0 }).count,
            labelSpread: (labelling.get(c.region.id) || { spread: 0 }).spread,
            kind,
            kindGuess: kind,
        });
    });

    // Biggest first: the review list should open on the shapes that matter.
    out.sort((a, b) => b.areaFraction - a.areaFraction);

    const flagged = flagRegions(out, opts);

    const byKind = {};
    out.forEach((r) => { byKind[r.kind] = (byKind[r.kind] || 0) + 1; });

    return {
        regions: out,
        palette: summarisePalette(out),
        // Which cells of the routing grid sit inside the building. Handed to
        // the walkable model so a promenade shape that runs off the sheet
        // contributes only the part that is actually indoors.
        inside,
        stats: {
            labelsDissolved,
            flagged,
            edges: imageData.edges ? null : (edges ? edgeReport : null),
            width,
            height,
            totalRegions: regions.length,
            kept: out.length,
            rejectedSmall,
            byKind,
            epsilon,
        },
    };
}

/**
 * Propose what a shape is.
 *
 * Every branch here is a guess the operator can see and change in one click.
 * The point is to make the common plan mostly right on first pass, not to be
 * clever — a wrong guess costs a click, whereas the old behaviour of silently
 * discarding a shape cost the whole shape.
 */
function guessKind(m, opts) {
    if (m.lineworkOnly) {
        if (m.touchesBorder) return 'exterior';
        // A sectioned wall is a long thin cavity, not a room.
        if (m.solidity < opts.minSolidity || m.aspect > opts.maxAspect) return 'service';
        return 'tenancy';
    }

    const sprawls = m.solidity < opts.minSolidity || m.aspect > opts.maxAspect;
    // Its outline encloses far more than the shape itself covers, which means
    // other shapes are standing inside it.
    const hollow = m.fillRatio != null && m.fillRatio < opts.minFillRatio;

    /*
     * Shape and size are asked BEFORE colour, because they are the more
     * reliable witness. A shape that is both large and sprawling is a
     * promenade whatever it is filled with: no single tenancy covers a
     * twentieth of a floor plate and also snakes. Checking this first is what
     * stops a pale-blue mall floor being proposed as the biggest shop in the
     * centre.
     *
     * Enclosure still has to agree, because the ground OUTSIDE the building
     * answers to large-and-hollow just as well as the promenade does — it wraps
     * the whole plan and has the building standing in it. Without that gate the
     * car park is proposed as mall floor and becomes walkable.
     */
    if (m.areaFraction > opts.maxTenancyArea && (sprawls || hollow)
        && m.enclosedShare >= opts.minEnclosedShare) return 'circulation';

    if (m.areaFraction > opts.maxAnyTenancyArea) {
        if (m.enclosedShare >= opts.minEnclosedShare) return 'circulation';
        return m.touchesBorder ? 'exterior' : 'circulation';
    }

    if (m.coloured) {
        // A wash that snakes or ribbons is a service corridor or a hatch, not a
        // shop. Note the thresholds are loose: this only changes the proposal.
        if (sprawls) return 'service';
        return 'tenancy';
    }

    if (m.enclosedShare >= opts.minEnclosedShare) {
        /*
         * A small compact cell inside the building is a shop even when it is
         * drawn in a neutral. Deciding otherwise costs rent-bearing area that
         * nobody can see is missing, whereas the opposite mistake puts an extra
         * shape in a list the operator is already reading.
         *
         * Ordered before the void test on purpose: the brightest neutral is
         * whatever pale fill happens to be on the sheet, so a single cream unit
         * can drag the void threshold far enough to swallow every grey shop
         * with it. Size is the steadier signal at this end of the scale.
         */
        if (!sprawls && !hollow && m.areaFraction <= opts.maxNeutralTenancyArea) return 'tenancy';

        // Plans that bother to distinguish a void from the mall floor do it by
        // shading it a step darker. Nothing else in the raster separates them.
        if (m.lum < m.brightestNeutral - opts.voidLuminanceDrop) return 'void';
        return 'circulation';
    }

    if (m.touchesBorder) return 'exterior';
    return m.areaFraction > 0.02 ? 'exterior' : 'service';
}

/**
 * Mark the shapes an operator should look at before publishing.
 *
 * A trace is never going to be perfect, because a plan does not always draw the
 * difference between one unit and two. What it CAN do is know where it is
 * unsure, and say so, instead of handing over two hundred shapes with no
 * indication of which four are wrong. Finding the four by eye on a sheet that
 * size is the part nobody does, so the errors ship.
 *
 * The evidence, strongest first:
 *
 *   two-numbers  Two unit numbers printed inside one shape. This is not a
 *                heuristic about proportions -- it is the sheet's own labelling
 *                saying there are two units here, and a shape that holds both
 *                of them has joined them. Nothing else on the plan is this
 *                direct.
 *   oversize     Far bigger than the other units. Catches the same fault on a
 *                sheet that does not print numbers, at the cost of a guess.
 *   no-number    A unit with no number, where the sheet numbers its units. A
 *                fragment left over from a split, usually.
 *   sliver       Too small and too thin to be a shop, next to shapes that are
 *                not. The other half of a split.
 *   hollow       Its outline encloses far more than it fills, so something else
 *                is standing inside it.
 *
 * A shape holding exactly ONE number is never flagged on size or shape. The
 * plan has already told us it is one unit, and an anchor store legitimately
 * covers twenty times what its neighbours do -- second-guessing that from
 * proportions would flag every department store on every sheet.
 */
function flagRegions(regions, opts) {
    const leasable = regions.filter((r) => isLeasable(r.kind));

    // Does this sheet number its units at all? A DXF section or an unlabelled
    // marketing plan does not, and on those the absence of a number means
    // nothing whatsoever.
    const numbered = leasable.filter((r) => r.labelCount === 1).length;
    const sheetNumbersUnits = leasable.length >= 8 && (numbered / leasable.length) >= 0.5;

    const areas = leasable.map((r) => r.areaFraction).sort((a, b) => a - b);
    const median = areas.length ? areas[Math.floor(areas.length / 2)] : 0;

    let count = 0;
    regions.forEach((r) => {
        const flags = [];
        const shop = isLeasable(r.kind);
        /*
         * The plan itself says this is one unit: either one label, or several
         * sitting together in one block -- a number with a trading name under
         * it. That testimony outranks any guess made from proportions, so it
         * silences the size and shape rules below. An anchor store legitimately
         * covers twenty times what its neighbours do, and flagging every
         * department store on every sheet would teach the operator to ignore
         * the flags, which costs more than it saves.
         */
        const oneUnitLabelled = r.labelCount === 1
            || (r.labelCount > 1 && r.labelSpread < opts.labelSplitSpread);

        if (shop && r.labelCount >= 2 && r.labelSpread >= opts.labelSplitSpread) {
            flags.push({
                code: 'two-numbers',
                severity: 'high',
                note: `${r.labelCount} separate unit numbers are printed inside this one shape,`
                    + ' at opposite ends of it — so it is almost certainly that many units'
                    + ' traced as one. Draw the missing unit in by hand: it is cut out of'
                    + ' this shape, so nothing ends up counted twice.',
            });
        }
        if (shop && !oneUnitLabelled && median > 0 && r.areaFraction > median * 4) {
            flags.push({
                code: 'oversize',
                severity: 'high',
                note: `About ${Math.round(r.areaFraction / median)} times the size of a typical`
                    + ' unit on this floor. Either an anchor, or several units traced as one.',
            });
        }
        if (shop && sheetNumbersUnits && r.labelCount === 0) {
            flags.push({
                code: 'no-number',
                severity: 'low',
                note: 'No unit number was found inside this shape, though most units on this'
                    + ' sheet carry one. It may be a leftover piece of its neighbour.',
            });
        }
        if (shop && !oneUnitLabelled && median > 0 && r.areaFraction < median * 0.2 && r.aspect > 3.5) {
            flags.push({
                code: 'sliver',
                severity: 'low',
                note: 'Much smaller and thinner than the units around it — often what is left'
                    + ' when one unit is traced as two.',
            });
        }
        if (shop && r.fillRatio != null && r.fillRatio < opts.minFillRatio) {
            flags.push({
                code: 'hollow',
                severity: 'low',
                note: 'Its outline encloses a good deal more than the shape itself covers,'
                    + ' so something else is standing inside it.',
            });
        }

        r.flags = flags;
        if (flags.length) count += 1;
    });

    return count;
}

/**
 * For each region, the share of its area that sits inside the building.
 *
 * Run on the coarse routing grid rather than the raster: the enclosure test
 * casts eight rays per cell, and doing that per pixel would be a hundred times
 * the work for an answer that is only ever used as a threshold.
 */
function enclosureByRegion(labels, width, height, colouredIds, opts) {
    const gridEdge = opts.routeGrid;
    const scale = Math.min(1, gridEdge / Math.max(width, height));
    const gw = Math.max(8, Math.round(width * scale));
    const gh = Math.max(8, Math.round(height * scale));

    // Dominant region per cell, and whether that cell is a coloured shape.
    const cellCounts = new Array(gw * gh);
    for (let i = 0; i < cellCounts.length; i++) cellCounts[i] = null;
    const isColouredCell = new Uint8Array(gw * gh);
    const dominant = new Int32Array(gw * gh).fill(-1);

    for (let y = 0; y < height; y++) {
        const gy = Math.min(gh - 1, Math.floor((y / height) * gh));
        for (let x = 0; x < width; x++) {
            const gx = Math.min(gw - 1, Math.floor((x / width) * gw));
            const g = (gy * gw) + gx;
            const id = labels[(y * width) + x];
            if (id < 0) continue;
            let m = cellCounts[g];
            if (!m) { m = new Map(); cellCounts[g] = m; }
            m.set(id, (m.get(id) || 0) + 1);
        }
    }
    for (let g = 0; g < cellCounts.length; g++) {
        const m = cellCounts[g];
        if (!m) continue;
        let bestId = -1;
        let bestN = 0;
        m.forEach((n, id) => { if (n > bestN) { bestN = n; bestId = id; } });
        dominant[g] = bestId;
        if (colouredIds.has(bestId)) isColouredCell[g] = 1;
    }

    const RAYS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
    const total = new Map();
    const insideCount = new Map();
    const insideCells = new Uint8Array(gw * gh);

    for (let gy = 0; gy < gh; gy++) {
        for (let gx = 0; gx < gw; gx++) {
            const g = (gy * gw) + gx;
            const id = dominant[g];
            if (id < 0) continue;
            total.set(id, (total.get(id) || 0) + 1);
            if (isColouredCell[g]) {
                insideCount.set(id, (insideCount.get(id) || 0) + 1);
                insideCells[g] = 1;
                continue;
            }

            let hits = 0;
            for (let r = 0; r < RAYS.length; r++) {
                const [dx, dy] = RAYS[r];
                let x = gx + dx;
                let y = gy + dy;
                while (x >= 0 && y >= 0 && x < gw && y < gh) {
                    if (isColouredCell[(y * gw) + x]) { hits += 1; break; }
                    x += dx;
                    y += dy;
                }
            }
            if (hits >= opts.minEnclosureRays) {
                insideCount.set(id, (insideCount.get(id) || 0) + 1);
                insideCells[g] = 1;
            }
        }
    }

    // Close the notches. A cell standing opposite the gap between two shops
    // loses the two rays that slip through it and fails the test even though it
    // is plainly in the middle of the mall. Left alone those failures line up
    // into stripes that cut the walkable area into disconnected segments.
    morphClose(insideCells, gw, gh, 2);

    const share = new Map();
    total.forEach((n, id) => share.set(id, (insideCount.get(id) || 0) / n));
    return { share, inside: { w: gw, h: gh, bits: packBits(insideCells) } };
}

/** In-place binary dilation/erosion over a Chebyshev disc of radius r. */
function morphPass(mask, w, h, r, dilate) {
    const out = new Uint8Array(mask.length);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let hit = dilate ? 0 : 1;
            for (let dy = -r; dy <= r && (dilate ? !hit : hit); dy++) {
                for (let dx = -r; dx <= r; dx++) {
                    const nx = x + dx;
                    const ny = y + dy;
                    // Outside the sheet reads as empty, which erodes the border.
                    const v = (nx < 0 || ny < 0 || nx >= w || ny >= h) ? 0 : mask[(ny * w) + nx];
                    if (dilate) { if (v) { hit = 1; break; } } else if (!v) { hit = 0; break; }
                }
            }
            out[(y * w) + x] = hit;
        }
    }
    mask.set(out);
}

function morphClose(mask, w, h, r) {
    morphPass(mask, w, h, r, true);
    morphPass(mask, w, h, r, false);
}

/**
 * Which fill colours the sheet uses, and how many shapes wear each.
 *
 * This drives the importer's colour mapper: a mall plan already colour-codes
 * its categories, so letting the operator say "purple is Fashion" once assigns
 * a hundred shops in one click instead of a hundred.
 */
function summarisePalette(regions) {
    const byColor = new Map();
    regions.forEach((r) => {
        const entry = byColor.get(r.colorKey)
            || { color: r.colorKey, count: 0, areaFraction: 0, coloured: r.coloured };
        entry.count += 1;
        entry.areaFraction += r.areaFraction;
        byColor.set(r.colorKey, entry);
    });
    return [...byColor.values()].sort((a, b) => b.count - a.count);
}

export function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
}
